import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({ baseURL: API_URL, withCredentials: true });

// Attach access token to every request
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("accessToken");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const { data } = await axios.post(
          `${API_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        localStorage.setItem("accessToken", data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        localStorage.removeItem("accessToken");
        // Don't navigate — let AuthContext.loadUser's catch handle it
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  },
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  register: (d) => api.post("/auth/register", d),
  login: (d) => api.post("/auth/login", d),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  verifyEmail: (d) => api.post("/auth/verify-email", d),
  resendOtp: () => api.post("/auth/resend-otp"),
  forgotPassword: (d) => api.post("/auth/forgot-password", d),
  resetPassword: (token, d) => api.put(`/auth/reset-password/${token}`, d),
  updateProfile: (d) => api.put("/auth/update-profile", d),
  changePassword: (d) => api.put("/auth/change-password", d),
};

// ── Bots ──────────────────────────────────────────────────────────────────────
export const botsAPI = {
  list: () => api.get("/bots"),
  create: (d) => api.post("/bots", d),
  get: (id) => api.get(`/bots/${id}`),
  update: (id, d) => api.put(`/bots/${id}`, d),
  delete: (id) => api.delete(`/bots/${id}`),
  embed: (id) => api.get(`/bots/${id}/embed`),
  stats: (id) => api.get(`/bots/${id}/stats`),
  addQA: (id, d) => api.post(`/bots/${id}/knowledge/qa`, d),
  addText: (id, d) => api.post(`/bots/${id}/knowledge/text`, d),
  scrapeUrl: (id, d) => api.post(`/bots/${id}/knowledge/url`, d),
  deleteKnowledge: (id, kid) => api.delete(`/bots/${id}/knowledge/${kid}`),
  toggleKnowledge: (id, kid) => api.put(`/bots/${id}/knowledge/${kid}/toggle`),
};

// ── Leads ─────────────────────────────────────────────────────────────────────
export const leadsAPI = {
  list: (p) => api.get("/leads", { params: p }),
  get: (id) => api.get(`/leads/${id}`),
  update: (id, d) => api.put(`/leads/${id}`, d),
  delete: (id) => api.delete(`/leads/${id}`),
  archive: (id) => api.put(`/leads/${id}/archive`),
  export: (p) =>
    api.get("/leads/export/csv", { params: p, responseType: "blob" }),
};

// ── Analytics ─────────────────────────────────────────────────────────────────
export const analyticsAPI = {
  overview: () => api.get("/analytics/overview"),
  chart: (p) => api.get("/analytics/chart", { params: p }),
  topQuestions: (p) => api.get("/analytics/top-questions", { params: p }),
};

// ── Conversations ─────────────────────────────────────────────────────────────
export const convsAPI = {
  list: (p) => api.get("/conversations", { params: p }),
  get: (id) => api.get(`/conversations/${id}`),
  delete: (id) => api.delete(`/conversations/${id}`),
};

// ── Billing ───────────────────────────────────────────────────────────────────
export const billingAPI = {
  plans: () => api.get("/billing/plans"),
  subscription: () => api.get("/billing/subscription"),
  dodoCreateSession: (d) => api.post("/billing/dodo/create-session", d),
  dodoPortal: () => api.post("/billing/dodo/portal"),
  cancel: () => api.post("/billing/cancel"),
};

// ── Meetings ──────────────────────────────────────────────────────────────────
export const meetingsAPI = {
  list: (p) => api.get("/meetings", { params: p }),
  get: (id) => api.get(`/meetings/${id}`),
  create: (d) => api.post("/meetings", d),
  update: (id, d) => api.put(`/meetings/${id}`, d),
  delete: (id) => api.delete(`/meetings/${id}`),
  calendarStatus: () => api.get("/meetings/calendar-status"),
  googleAuthUrl: () => api.get("/meetings/google/auth-url"),
  googleDisconnect: () => api.delete("/meetings/google/disconnect"),
};

export default api;
